/**
 * Translation access for the public booking screens.
 *
 * Three agents are editing this tree at once, so `src/i18n/*.json` is
 * off-limits during the build and the keys this feature introduces are handed
 * to the orchestrator separately (see the i18n handoff in the brief). Until
 * they land, `MessageKey` — which is derived from `en.json` — does not know
 * about them, so they are declared here and merged into the accepted key type.
 *
 * The union below is the whole contract: adding a `t()` call with a key that
 * is neither shipped nor listed here is a compile error, which is exactly the
 * safety net the real `MessageKey` provides.
 */

import { useCallback } from 'react';
import { useI18n } from '@/shared/i18n/useT';
import type { MessageKey, TranslateValues } from '@/shared/i18n';

/** Keys pending a merge into `src/i18n/{en,es,pt}.json`. */
export type PendingMessageKey =
  // page + navigation
  | 'publicBooking.chooseService'
  | 'publicBooking.chooseDate'
  | 'publicBooking.chooseTime'
  | 'publicBooking.yourDetails'
  | 'publicBooking.hostedBy'
  | 'publicBooking.noServices'
  | 'publicBooking.notFoundBody'
  | 'publicBooking.backToServices'
  | 'publicBooking.changeTime'
  | 'publicBooking.free'
  | 'publicBooking.notes'
  | 'publicBooking.notesPlaceholder'
  | 'publicBooking.confirming'
  | 'publicBooking.signedInAs'
  // calendar
  | 'publicBooking.calendar.label'
  | 'publicBooking.calendar.previousMonth'
  | 'publicBooking.calendar.nextMonth'
  | 'publicBooking.calendar.noSlotsThisMonth'
  | 'publicBooking.calendar.pickADay'
  | 'publicBooking.calendar.timesOn'
  | 'publicBooking.calendar.slotsAvailable'
  | 'publicBooking.calendar.dayUnavailable'
  // timezone
  | 'publicBooking.timezone.label'
  | 'publicBooking.timezone.yours'
  | 'publicBooking.timezone.professional'
  | 'publicBooking.timezone.shownIn'
  // location
  | 'publicBooking.location.custom'
  | 'publicBooking.location.in_person'
  | 'publicBooking.location.phone'
  | 'publicBooking.location.video'
  | 'publicBooking.location.label'
  // confirmation
  | 'publicBooking.confirmed.title'
  | 'publicBooking.confirmed.body'
  | 'publicBooking.confirmed.addToCalendar'
  | 'publicBooking.confirmed.manage'
  | 'publicBooking.confirmed.manageHint'
  | 'publicBooking.confirmed.copyLink'
  | 'publicBooking.confirmed.copied'
  | 'publicBooking.confirmed.bookAnother'
  // manage
  | 'publicBooking.manage.title'
  | 'publicBooking.manage.description'
  | 'publicBooking.manage.tokenMissing'
  | 'publicBooking.manage.current'
  | 'publicBooking.manage.cancel'
  | 'publicBooking.manage.cancelTitle'
  | 'publicBooking.manage.cancelReason'
  | 'publicBooking.manage.cancelReasonPlaceholder'
  | 'publicBooking.manage.cancelConfirm'
  | 'publicBooking.manage.cancelledTitle'
  | 'publicBooking.manage.cancelledBody'
  | 'publicBooking.manage.keep'
  | 'publicBooking.manage.reschedule'
  | 'publicBooking.manage.rescheduleTitle'
  | 'publicBooking.manage.rescheduleConfirm'
  | 'publicBooking.manage.rescheduledTitle'
  | 'publicBooking.manage.rescheduledBody'
  | 'publicBooking.manage.alreadyCancelled'
  // my bookings
  | 'publicBooking.mine.upcoming'
  | 'publicBooking.mine.past'
  | 'publicBooking.mine.noneUpcoming'
  | 'publicBooking.mine.nonePast'
  | 'publicBooking.mine.manage'
  // failures
  | 'publicBooking.error.rateLimited'
  | 'publicBooking.error.accountRequired'
  | 'publicBooking.error.sessionRequired'
  | 'publicBooking.error.cancellationCutoff'
  | 'publicBooking.error.outsideWindow'
  | 'publicBooking.error.outsideAvailability'
  | 'publicBooking.error.emailMismatch'
  | 'publicBooking.error.unauthorized'
  | 'publicBooking.error.bookingNotFound'
  | 'publicBooking.error.network'
  | 'publicBooking.error.slotTakenRefreshed';

export type BookingMessageKey = MessageKey | PendingMessageKey;

export type BookingTranslate = (
  key: BookingMessageKey,
  values?: TranslateValues,
) => string;

/**
 * `t()` widened to the pending keys. The cast is the single place the
 * temporary key gap is bridged; when the orchestrator merges the JSON the
 * union collapses back into `MessageKey` and nothing else changes.
 */
export function useBookingT(): BookingTranslate {
  const { t } = useI18n();
  return useCallback<BookingTranslate>(
    (key, values) => t(key as MessageKey, values),
    [t],
  );
}
